import type { FormStateValue } from '@de/providers/DataSourceManager.types';
import { formStateValueToKeys } from '@de/utils/form-state';
import { isAffirmative } from '@de/utils/affirmative';

/**
 * Resolve a dot path against a row and return the stats-like object it
 * points at (e.g. `institutionalStats`). The offerings coercer hoists
 * that map onto the row root, so the path is usually a single segment,
 * but dotted paths are supported defensively. Returns `undefined` when
 * the path misses or lands on a non-object.
 */
function resolveStatsObject(
  row: unknown,
  path: string,
): Record<string, unknown> | undefined {
  if (row === null || typeof row !== 'object') {
    return undefined;
  }
  let current: unknown = row;
  for (const segment of path.split('.')) {
    if (segment.length === 0) continue;
    if (current === null || typeof current !== 'object') {
      return undefined;
    }
    current = (current as Record<string, unknown>)[segment];
  }
  return current !== null && typeof current === 'object'
    ? (current as Record<string, unknown>)
    : undefined;
}

/**
 * `true` when `statsObject[key]` is an affirmative signal. Accepts both
 * the offerings `{ label, value }` stat shape (reads `.value`) and a
 * bare boolean/string flag, so a row matches whether the filter key
 * points at a labeled stat or a raw tag field.
 */
function rowHasTag(statsObject: Record<string, unknown>, key: string): boolean {
  const entry = statsObject[key];
  if (entry !== null && typeof entry === 'object') {
    return isAffirmative((entry as { value?: unknown }).value);
  }
  return isAffirmative(entry);
}

/**
 * Count how many of the active filter keys a row affirms at `path`.
 * A row with no stats object (or none of the keys) scores 0.
 */
export function tagMatchScore(
  row: unknown,
  activeKeys: ReadonlyArray<string>,
  path: string,
): number {
  if (activeKeys.length === 0) {
    return 0;
  }
  const stats = resolveStatsObject(row, path);
  if (!stats) {
    return 0;
  }
  let score = 0;
  for (const key of activeKeys) {
    if (rowHasTag(stats, key)) {
      score += 1;
    }
  }
  return score;
}

/**
 * Re-rank rows by how many active filter pills they satisfy — the
 * "best for you" behavior of the legacy Super Sonic EL
 * (`EditorialListingsBestForYou`): rows matching more selected tags
 * float to the top, the rest sink but **stay visible**. This is a
 * ranking, not a filter — nothing is dropped.
 *
 * Stable: rows with an equal match count keep their original relative
 * order (the index tiebreaker guarantees it regardless of engine sort
 * stability), and an empty `activeKeys` returns the original order
 * verbatim (matches legacy's `return 0` short-circuit). Pure and total
 * — malformed rows score 0 rather than throwing. Client-side only.
 */
export function rankItemsByTags(
  items: ReadonlyArray<unknown>,
  activeKeys: ReadonlyArray<string>,
  path: string,
): unknown[] {
  if (activeKeys.length === 0) {
    return items.slice();
  }
  return items
    .map((row, index) => ({
      row,
      index,
      score: tagMatchScore(row, activeKeys, path),
    }))
    .sort((a, b) => b.score - a.score || a.index - b.index)
    .map((entry) => entry.row);
}

/**
 * Read the active-filter value the filter ToggleGroup writes into form state as
 * a clean key list (trimmed, empties dropped). `undefined` / blank → `[]`.
 *
 * Delegates rather than parsing here: form state can hold either a list or the
 * comma-joined string a multi-value control wrote before it could, and how those
 * two are read is one decision, not one per consumer.
 */
export function parseActiveFilterKeys(
  raw: FormStateValue | undefined,
): string[] {
  return formStateValueToKeys(raw);
}
